import { ReactNode, useState, useEffect, Suspense } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { PanelLeftClose, PanelLeftOpen, LogOut, Headset, X, Menu, ChevronRight } from 'lucide-react';
import { Tooltip, ActionModal, LoadingSpinner } from '@/components/ui';

export interface UnifiedSidebarMenuItem {
  path: string;
  label: string;
  icon: any;
  end?: boolean;
  badge?: number;
}

export interface UnifiedSidebarProps {
  isCollapsed: boolean;
  setIsCollapsed: (v: boolean) => void;
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (v: boolean) => void;
  brandLink: string;
  menuItems: UnifiedSidebarMenuItem[];
  breadcrumbs: { label: string; to: string }[];
  headerActions?: ReactNode;
  onSignOut: () => Promise<void>;
  showHelp?: boolean;
  extraFooterItems?: ReactNode;
}

export function UnifiedSidebar({
  isCollapsed,
  setIsCollapsed,
  mobileMenuOpen,
  setMobileMenuOpen,
  brandLink,
  menuItems,
  breadcrumbs,
  headerActions,
  onSignOut,
  showHelp = false,
  extraFooterItems,
}: UnifiedSidebarProps) {
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMobileMenuOpen(false);
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'b') {
        event.preventDefault();
        setIsCollapsed(!isCollapsed);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isCollapsed, setIsCollapsed, setMobileMenuOpen]);

  const handleSignOut = async () => {
    await onSignOut();
    setIsLogoutConfirmOpen(false);
    setMobileMenuOpen(false);
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `group relative flex items-center rounded-xl py-3 text-sm font-medium transition-all duration-200 ${isCollapsed ? 'justify-center w-11 px-0 mx-auto' : 'gap-3 w-full px-3'} ${
      isActive
        ? 'bg-brand-lime/10 font-semibold ring-1 ring-gray-200 bg-gray-50'
        : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
    }`;

  const sidebarContent = (
    <div className="flex h-full w-full flex-col bg-white">
      <div
        className={`flex h-16 shrink-0 items-center border-b border-gray-100 px-4 ${isCollapsed ? 'justify-center' : 'justify-between'}`}
      >
        {!isCollapsed && (
          <Link
            to={brandLink}
            className="flex items-center overflow-hidden whitespace-nowrap pl-1 transition-opacity hover:opacity-80"
          >
            <span className="heading-3 leading-tight">
              <span className="text-slate-950">Mondi</span>
              <span className="text-lime-500">vago</span>
            </span>
          </Link>
        )}
        <Tooltip content={isCollapsed ? 'Expand (Ctrl+B)' : 'Collapse (Ctrl+B)'} position="right">
          <button
            type="button"
            onClick={() => {
              setIsCollapsed(!isCollapsed);
            }}
            className={`rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-900 ${isCollapsed ? 'mx-auto' : ''}`}
          >
            {isCollapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
          </button>
        </Tooltip>
      </div>

      <nav
        className={`flex-1 space-y-1 p-3 overflow-x-hidden ${isCollapsed ? 'overflow-x-visible overflow-y-visible' : 'overflow-y-auto'}`}
      >
        {menuItems.map((item) => {
          const Icon = item.icon;
          return (
            <Tooltip key={item.path} content={item.label} position="right" className="w-full" disabled={!isCollapsed}>
              <NavLink to={item.path} end={item.end ?? false} className={navLinkClass}>
                <Icon className="h-5 w-5 shrink-0" />
                {!isCollapsed && <span>{item.label}</span>}
                {item.badge !== undefined && item.badge > 0 && (
                  <span
                    className={`flex items-center justify-center rounded-full bg-lime-800 text-white text-[10px] font-black leading-none ${
                      isCollapsed
                        ? 'absolute -right-0.5 -top-0.5 h-4 min-w-[16px] px-1'
                        : 'ml-auto h-5 min-w-[20px] px-1.5'
                    }`}
                  >
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </NavLink>
            </Tooltip>
          );
        })}
      </nav>

      <div className="p-3 border-t border-gray-100 space-y-2">
        {extraFooterItems}

        {showHelp && (
          <Tooltip content="Help Center" position="right" className="w-full" disabled={!isCollapsed}>
            <Link
              to="/help-center"
              className={`group relative flex items-center rounded-xl py-3 nav-label-inactive transition-colors hover:bg-gray-100 hover:text-gray-900 ${isCollapsed ? 'justify-center w-11 px-0 mx-auto' : 'w-full gap-3 px-3'}`}
            >
              <Headset className="h-5 w-5 shrink-0" />
              {!isCollapsed && <span>Help</span>}
            </Link>
          </Tooltip>
        )}

        <Tooltip content="Sign Out" position="right" className="w-full" disabled={!isCollapsed}>
          <button
            type="button"
            onClick={() => {
              setIsLogoutConfirmOpen(true);
            }}
            className={`group relative flex items-center rounded-xl py-3 text-sm font-medium text-gray-600 transition-colors hover:bg-red-50 hover:text-red-700 ${isCollapsed ? 'justify-center w-11 px-0 mx-auto' : 'w-full gap-3 px-3'}`}
          >
            <LogOut className="h-5 w-5 shrink-0" />
            {!isCollapsed && <span>Sign Out</span>}
          </button>
        </Tooltip>
      </div>
    </div>
  );

  return (
    <>
      <aside
        className={`fixed inset-y-0 left-0 z-50 hidden border-r border-gray-200 bg-gray-50 transition-all duration-300 md:flex ${isCollapsed ? 'w-20' : 'w-64'}`}
      >
        {sidebarContent}
      </aside>

      <header
        className={`fixed inset-x-0 top-0 z-40 hidden h-16 items-center border-b border-gray-200 bg-white/80 backdrop-blur-md transition-all duration-300 md:flex ${isCollapsed ? 'md:pl-20' : 'md:pl-64'}`}
      >
        <div className="mx-auto flex w-full items-center justify-between transition-all duration-300 max-w-[1600px] px-10">
          <nav aria-label="breadcrumb" className="flex items-center gap-1.5">
            {breadcrumbs.map((crumb, idx) => {
              const isLast = idx === breadcrumbs.length - 1;
              return (
                <span key={crumb.to} className="flex items-center gap-1.5">
                  {idx > 0 && <ChevronRight className="h-3.5 w-3.5 text-gray-400" />}
                  {isLast ? (
                    <span className="text-sm font-semibold text-gray-900">{crumb.label}</span>
                  ) : (
                    <Link to={crumb.to} className="nav-label-inactive transition-colors hover:text-gray-900">
                      {crumb.label}
                    </Link>
                  )}
                </span>
              );
            })}
          </nav>
          {headerActions}
        </div>
      </header>

      <header className="fixed inset-x-0 top-0 z-40 border-b border-gray-200 bg-white pt-[env(safe-area-inset-top)] md:hidden">
        <div className="mx-auto flex h-14 items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                setMobileMenuOpen(!mobileMenuOpen);
              }}
              className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-50 text-gray-600 transition-colors hover:bg-gray-100"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <Link to={brandLink} className="flex items-center gap-2">
              <span className="heading-4">
                <span className="text-slate-950">Mondi</span>
                <span className="text-lime-500">vago</span>
              </span>
            </Link>
          </div>
          {headerActions}
        </div>
      </header>

      {mobileMenuOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm md:hidden"
            onClick={() => {
              setMobileMenuOpen(false);
            }}
          />
          <div className="fixed inset-y-0 left-0 z-50 w-4/5 max-w-sm bg-white shadow-2xl md:hidden">
            {sidebarContent}
          </div>
        </>
      )}

      <ActionModal
        isOpen={isLogoutConfirmOpen}
        onClose={() => {
          setIsLogoutConfirmOpen(false);
        }}
        title="Confirm sign out?"
        description="You will be redirected to the Mondivago homepage."
        onConfirm={() => {
          void handleSignOut();
        }}
        confirmLabel="Sign Out"
        variant="danger"
      />

      <main
        className={`min-h-screen bg-white font-sans pb-6 pt-[calc(env(safe-area-inset-top)+4rem)] transition-all duration-300 md:pt-16 w-full max-w-full ${isCollapsed ? 'md:pl-20' : 'md:pl-64'}`}
      >
        <div className="mx-auto transition-all duration-300 max-w-[1600px] px-4 sm:px-6 md:px-10 min-w-0 w-full">
          <Suspense
            fallback={
              <div className="flex min-h-[60vh] w-full items-center justify-center">
                <LoadingSpinner />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </div>
      </main>
    </>
  );
}
